import React from 'react';
import { Alert } from '@material-ui/lab';
import { Typography, Grid, Backdrop } from '@material-ui/core';
import { Modal, Box, Card, CardContent, Button } from '@mui/material';
import { PieChart, Pie, Cell, Sector, Tooltip, ResponsiveContainer } from 'recharts';
import {
  COLOR_PRIMARY,
  COLOR_ACCENT_1,
  COLOR_ACCENT_2,
  COLOR_PRIMARY_LIGHT,
  MID_GREY,
  COLORS_PROPERTY_SIZE,
  COLOR_TOOLTIP_BACKGROUND,
  sampleOccupancyRateData,
  sampleMedianNightlyRateData,
  sampleRevenueData,
  COLOR_E,
} from 'src/const';
import { Listing, OccupancyRate, ADR, Revenue, BookingsPerMonth } from 'src/interfaces/idashboard';
import { TooltipCard, DigestCard } from '../HOC/Cards';
import { InfoTooltip } from '../HOC/Tooltips';
import { getMonthlyKeyMetrics } from 'src/utils/functions';

import LockSharpIcon from '@material-ui/icons/LockSharp';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { AdvancedPointerViewPropTypes } from 'src/interfaces/iproptypes';

const pieLabel = {
  studio: 'Studio',
  private_room: 'Private Room',
  '1': '1 Bedroom',
  '2': '2 Bedrooms',
  '3': '3 Bedrooms',
  '4': '4 Bedrooms',
  '5': '5 Bedrooms',
  '6+': '6+ Bedrooms',
};

const roomTypePiechart = {
  0: '1',
  1: '2',
  2: '3',
  3: '4',
  4: '5',
  5: '6+',
  6: 'P',
  7: 'S',
};

const RADIAN = Math.PI / 180;

const AdvancedPointerView = ({
  advancedViewOpen,
  setAdvancedViewOpen,
  data,
  historicalPerfTrigger,
  setHistoricalPerfTrigger,
  setSubscriptionModalOpen,
  dynamicOccupancyRate,
  dynamicADR,
  dynamicRev,
  ownerTotalListings,
  currency,
  isDisabled,
  handleOnSampleClick,
  setShowSearchCreditDeductWarningModal,
  setTooltipOpen,
  // estimatorSampleListingData,
}: AdvancedPointerViewPropTypes) => {
  const pieDataPercentageObjectArrayDemo = [
    { name: 'Studio', value: 40 },
    { name: 'Private', value: 30 },
    { name: '1', value: 15 },
    { name: '2', value: 5 },
    { name: '3', value: 5 },
    { name: '4', value: 5 },
  ];
 
  let room_types = {
    private_room: 0,
    studio: 0,
    '1': 0,
    '2': 0,
    '3': 0,
    '4': 0,
    '5': 0,
    '6': 0,
  };

  const room_types_arr_obj: any = [];

  if (ownerTotalListings && ownerTotalListings.length > 0) {
    ownerTotalListings.forEach((listing, index) => {
      if (listing.room_type === 'private_room') {
        room_types.private_room = room_types.private_room + 1;
      }
    });

    for (const listing of ownerTotalListings) {
      //checking if the room is entire home or condo
      if (
        !isNaN(Number(listing.bedrooms)) &&
        listing.room_type !== 'private_room' &&
        ((listing.room_type && listing.room_type === 'entire_home') ||
          (listing.property_type && listing.property_type === 'condo'))
      ) {
        if (Number(listing.bedrooms) >= 6) {
          room_types['6'] = room_types['6'] + 1;
        } else {
          room_types[listing.bedrooms] = room_types[listing.bedrooms] + 1;
        }
      }
    }

    for (const room_type in room_types) {
      room_types_arr_obj.push({
        name: room_type,
        value: room_types[room_type],
      });
    }
  }

  // const propertySizeData = getSupplyByPropertySize(listings.data);

  const INACTIVE_COLORS = Array(10).fill(COLOR_PRIMARY_LIGHT);
  const INNERPIE_COLORS = COLORS_PROPERTY_SIZE;

  const renderPiechartLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    if (percent > 0.03) {
      return (
        <text
          x={x}
          y={y}
          fill="white"
          textAnchor="middle"
          style={{ fontSize: '0.75rem', fontFamily: 'Roboto' }}
          dominantBaseline="central"
        >
          {`${roomTypePiechart[index]}`}
        </text>
      );
    }
    return;
  };

  const CustomTooltip = ({ active, payload, label, nameObject }) => {
    if (active) {
      return (
        <Card>
          <CardContent style={{ background: COLOR_TOOLTIP_BACKGROUND }}>
            <Typography variant="body1" style={{ fontWeight: 'bolder' }}>
              {nameObject[payload[0].name]}
            </Typography>
            <Typography variant="body2" style={{ fontWeight: 500 }}>
              {payload[0].value} listings
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  return (
    <Modal
      open={advancedViewOpen}
      onClose={() => {
        setAdvancedViewOpen(false);
        setHistoricalPerfTrigger(false);
      }}
    >
      <Box
        sx={{
          background: '#fff',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80vw',
          maxHeight: '80vh',
          overflow: 'auto',
          padding: '50px 40px',
          border: 'none',
          outline: 'none',
          borderRadius: 4,
          zIndex: 9999999,
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={historicalPerfTrigger ? 5 : 3}>
            <Typography
              variant={historicalPerfTrigger ? 'h5' : 'h4'}
              style={{ fontWeight: 'bold', color: '#000' }}
            >
              {historicalPerfTrigger ? (
                <>
                  Historical Performance <br /> {'(1 year)'}
                </>
              ) : (
                data['host_name']
              )}
            </Typography>
            {!historicalPerfTrigger &&
              (isDisabled ? (
                <Grid container direction="row" spacing={1} alignItems="center">
                  <Grid item>
                    <LockSharpIcon style={{ color: COLOR_PRIMARY }} />
                  </Grid>
                  <Grid item>
                    <Typography variant="h5" style={{ color: COLOR_PRIMARY }}>
                      Listings
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="h5" style={{ color: COLOR_PRIMARY }}>
                  {ownerTotalListings.length}{' '}
                  {ownerTotalListings.length > 1 ? 'Listings' : 'Listing'}
                </Typography>
              ))}
          </Grid>
          <Grid
            item
            xs={12}
            md={historicalPerfTrigger ? 1 : 3}
            style={{
              height: '100%',
              position: 'relative',
            }}
          >
            {!historicalPerfTrigger && (
              <div style={{ pointerEvents: isDisabled ? 'none' : 'auto' }}>
                <Typography style={{ textAlign: 'center' }}>Property Size Breakdown</Typography>
                <ResponsiveContainer
                  width={'100%'}
                  height={200}
                  style={{ filter: isDisabled ? 'opacity(0.4)' : 'opacity(1)' }}
                >
                  <PieChart margin={{ top: 0, left: 0, right: 0, bottom: 0 }}>
                    <Pie
                      data={isDisabled ? pieDataPercentageObjectArrayDemo : room_types_arr_obj}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={60}
                      isAnimationActive={false}
                      labelLine={false}
                      label={renderPiechartLabel}
                    >
                      {isDisabled
                        ? pieDataPercentageObjectArrayDemo.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={INNERPIE_COLORS[index % INACTIVE_COLORS.length]}
                            />
                          ))
                        : room_types_arr_obj.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={INNERPIE_COLORS[index % INACTIVE_COLORS.length]}
                            />
                          ))}
                    </Pie>
                    <Tooltip
                      content={<CustomTooltip active payload label nameObject={pieLabel} />}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            {isDisabled && !historicalPerfTrigger && (
              <Typography
                variant="h5"
                style={{
                  color: COLOR_E,
                  fontWeight: 600,
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: '#fff8',
                  zIndex: 100,
                }}
              >
                <Button
                  onClick={() => {
                    handleOnSampleClick && handleOnSampleClick();
                  }}
                  style={{ all: 'unset', cursor: 'pointer' }}
                >
                  PREMIUM
                </Button>
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {!isDisabled ? (
              <DigestCard
                title="Revenue"
                data={
                  !historicalPerfTrigger
                    ? dynamicRev
                    : (getMonthlyKeyMetrics('revenue', [data], true, true, 0.5) as Revenue[])
                }
                color={COLOR_PRIMARY}
                isDisabled={false}
                isLoggedIn={true}
                multipleMarkersCallLoading={false}
                dynamicTrigger={false}
                currency={currency}
                setSubscriptionModalOpen={
                  setSubscriptionModalOpen ? setSubscriptionModalOpen : setAdvancedViewOpen
                }
                // dynamicKeyMetrics={dynamicRevenueTrend}
                isAnnualRevenue={true}
                showDeltaText={true}
                isFromHostInsights={true}
                isFromListingHistory={historicalPerfTrigger}
              />
            ) : (
              <DigestCard
                title="Revenue"
                data={sampleRevenueData}
                color={COLOR_PRIMARY}
                isDisabled={true}
                isLoggedIn={true}
                multipleMarkersCallLoading={false}
                dynamicTrigger={false}
                currency={currency}
                setSubscriptionModalOpen={
                  setSubscriptionModalOpen ? setSubscriptionModalOpen : setAdvancedViewOpen
                }
                setShowSearchCreditDeductWarningModal={
                  setShowSearchCreditDeductWarningModal
                    ? setShowSearchCreditDeductWarningModal
                    : undefined
                }
                // dynamicKeyMetrics={dynamicRevenueTrend}
                handleOnSampleClick={handleOnSampleClick}
                isFromHostInsights={true}
                isFromListingHistory={historicalPerfTrigger}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {!isDisabled ? (
              <DigestCard
                title="Occupancy Rate"
                data={
                  !historicalPerfTrigger
                    ? dynamicOccupancyRate
                    : (getMonthlyKeyMetrics(
                          'occupancy',
                          [data],
                          true,
                          false,
                          0.5,
                          // true
                        ) as OccupancyRate[])
                }
                color={COLOR_ACCENT_1}
                isDisabled={false}
                isLoggedIn={true}
                multipleMarkersCallLoading={false}
                dynamicTrigger={false}
                currency={currency}
                setSubscriptionModalOpen={
                  setSubscriptionModalOpen ? setSubscriptionModalOpen : setAdvancedViewOpen
                }
                // dynamicKeyMetrics={dynamicOccupancyRateTrend}
                isFromHostInsights={true}
                isFromListingHistory={historicalPerfTrigger}
              />
            ) : (
              <DigestCard
                title="Occupancy Rate"
                data={sampleOccupancyRateData}
                color={COLOR_ACCENT_1}
                isDisabled={true}
                isLoggedIn={true}
                multipleMarkersCallLoading={false}
                dynamicTrigger={false}
                currency={currency}
                setSubscriptionModalOpen={
                  setSubscriptionModalOpen ? setSubscriptionModalOpen : setAdvancedViewOpen
                }
                setShowSearchCreditDeductWarningModal={
                  setShowSearchCreditDeductWarningModal
                    ? setShowSearchCreditDeductWarningModal
                    : undefined
                }
                // dynamicKeyMetrics={dynamicOccupancyRateTrend}
                handleOnSampleClick={handleOnSampleClick}
                isFromHostInsights={true}
                isFromListingHistory={historicalPerfTrigger}
              />
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            {!isDisabled ? (
              <DigestCard
                title="Median Nightly Rate"
                data={
                  !historicalPerfTrigger
                    ? dynamicADR
                    : (getMonthlyKeyMetrics('adr', [data], true, false, 0.5) as ADR[])
                }
                color={COLOR_ACCENT_2}
                isDisabled={false}
                isLoggedIn={true}
                multipleMarkersCallLoading={false}
                dynamicTrigger={false}
                currency={currency}
                setSubscriptionModalOpen={
                  setSubscriptionModalOpen ? setSubscriptionModalOpen : setAdvancedViewOpen
                }
                // dynamicKeyMetrics={dynamicMedianDailyRateTrend}
                isFromHostInsights={true}
                isFromListingHistory={historicalPerfTrigger}
              />
            ) : (
              <DigestCard
                title="Median Nightly Rate"
                data={sampleMedianNightlyRateData}
                color={COLOR_ACCENT_2}
                isDisabled={true}
                isLoggedIn={true}
                multipleMarkersCallLoading={false}
                dynamicTrigger={false}
                currency={currency}
                setSubscriptionModalOpen={
                  setSubscriptionModalOpen ? setSubscriptionModalOpen : setAdvancedViewOpen
                }
                setShowSearchCreditDeductWarningModal={
                  setShowSearchCreditDeductWarningModal
                    ? setShowSearchCreditDeductWarningModal
                    : undefined
                }
                // dynamicKeyMetrics={dynamicMedianDailyRateTrend}
                handleOnSampleClick={handleOnSampleClick}
                isFromHostInsights={true}
                isFromListingHistory={historicalPerfTrigger}
              />
            )}
          </Grid>
          <Alert severity="info">Empty month on the graph refers to data not being available</Alert>
        </Grid>

        <div
          style={{ position: 'absolute', top: '10px', right: '10px' }}
          onClick={() => {
            setTooltipOpen(true);
            setAdvancedViewOpen(false);
            setHistoricalPerfTrigger(false);
          }}
        >
          <CloseRoundedIcon style={{ color: MID_GREY, cursor: 'pointer' }} />
        </div>
      </Box>
    </Modal>
  );
};

export default AdvancedPointerView;
